// NOTE: TeacherDashboardLesson.attendanceBadge is now wired from row.attendance_badge.
// The Teacher Dashboard timetable still uses legacy useLessons; do not remove legacy
// timetable data paths until the dedicated migration step is completed.
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import { supabase } from "@flexwise/shared/lib/supabaseClient";
import { notificationsApi } from "../../notifications/api/notificationsApi";
import type { NotificationHistoryPayload } from "../../notifications/types/notificationTypes";

export interface TeacherProfile {
  id: string;
  schoolId: string;
  firstName: string;
  lastName: string;
  email?: string | null;
}

export interface TeacherRole {
  id: string;
  name: string;
}

export interface TeacherClassLink {
  classId: string;
  staffId: string;
  isClassTeacher: boolean;
  role: string;
  className: string;
}

export interface AttendanceBadge {
  total_students: number;
  present_count: number;
  late_count: number;
  absent_count: number;
  attendance_status: string; // e.g. 'none' | 'complete' | 'incomplete'
}

export interface TeacherDashboardLesson {
  lessonId: string;
  schoolId: string;
  lessonDate: string;
  startDatetime: string;
  endDatetime: string;
  primaryTeacherId: string | null;
  teacherIds: string[];
  teacherKurzels: string[];
  subjectName: string | null;
  subjectAbbreviation: string | null;
  courseId: string | null;
  courseName: string | null;
  classId: string | null;
  className: string | null;
  roomId: string | null;
  roomName: string | null;
  roomNumber: string | null;
  substituteType: string | null;
  substitutePrimaryTeacherId: string | null;
  substituteBegleitungTeacherIds: string[];
  substituteBegleitungTeacherKurzels: string[];
  notes: string | null;
  attendanceTaken: boolean;
  teacherRole:
    | "primary"
    | "supporting"
    | "substitute_primary"
    | "substitute_begleitung";
  // Absent teacher IDs (UUIDs) - use for reliable logic
  absentTeacherIds?: string[];
  // Absent teacher kurzels - use for display only
  absentTeacherKurzels?: string[];
  // Original co-teachers who are NOT absent (for substitutes to see who they teach with)
  originalCoteacherKurzels?: string[];
  // Room change fields
  originalRoomNumber?: string | null;
  substituteRoomNumber?: string | null;
  hasRoomChange?: boolean;
  // Substitute comment
  substituteComment?: string | null;
  // Substitution is_cancelled flag (reliable cancellation indicator)
  substituteIsCancelled?: boolean;
  // Derived from fn_teacher_dashboard_initial_load.lessons_today[*].attendance_badge
  attendanceBadge?: AttendanceBadge | null;
  // Partial absence reason for the current teacher (e.g. 'Hilfekonferenz')
  // Only set when the teacher has a PARTIAL absence covering this lesson
  teacherAbsenceReason?: string | null;
  // Period number from schedule_periods.block_number (e.g. 1, 2, 3...)
  periodNumber?: number | null;
  // NC item support: item_type (e.g. 'lesson', 'pause', 'vorbereitung', 'bereitschaft', 'termin', 'course')
  itemType?: string | null;
  // NC item support: meeting_name from course_schedules (e.g. 'Team 1-2')
  meetingName?: string | null;
  // Band assignment (Leseband/Förderband) — present when this lesson's
  // (class_id, day_of_week, period_number) matches an active
  // baende_lesson_assignments row. Populated by Phase 4 of
  // fn_teacher_dashboard_initial_load.
  bandAssignment?: {
    assignmentId: string;
    bandId: string;
    bandName: string;
    bandIcon: string | null;
    bandColor: string | null;
    // True when a band_log_entries row (status='completed') exists for
    // this (assignment_id, lesson_id) pair — flips the icon from outline
    // to solid in LessonSchedule.
    completed: boolean;
  } | null;
}

export interface ClassAbsenceRow {
  id: string;
  classIds: string[];
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  absenceType: string | null;
  destination: string | null;
  accompanyingStaffIds: string[];
  classNames: string[];
  attendanceRequired: boolean;
}

export interface TripAttendanceSummary {
  present: number;
  late: number;
  absent: number;
  total: number;
}

export interface RegistrationPeriod {
  id: string;
  title: string;
  status: string;
  openedAt: string | null;
  closedAt: string | null;
  courseStartDate?: string | null;
  semesterId?: string | null;
}

export interface TeacherDashboardInitialLoadResponse {
  teacherProfile: TeacherProfile;
  roles: TeacherRole[];
  classLinks: TeacherClassLink[];
  collectingPeriods: RegistrationPeriod[];
  wuenscheRegistrationPeriods: RegistrationPeriod[];
  lessonsToday: TeacherDashboardLesson[];
  classAbsencesToday: ClassAbsenceRow[];
  tripAttendanceCounts: Record<string, TripAttendanceSummary>;
  /** Supervision duties for the day. Populated by
   *  fn_teacher_dashboard_initial_load v2 (bundled from the 3 client-side
   *  aufsichten_assignments + aufsichten_substitutions queries).
   *  Shape matches AufsichtDashboardRow from useTeacherDashboardData — not
   *  imported here to avoid a circular dep (the hook imports from this file). */
  aufsichten: Array<{
    id: string;
    startTime: string;
    endTime: string;
    ortName: string;
    zeitLabel: string;
    isSubstitute: boolean;
    isCancelled: boolean;
  }>;
  /** Per-module feature toggles for this school. Bundled from
   *  fn_get_school_module_settings so getSchoolModuleSettings can short-circuit
   *  to the cache. Shape: { [module]: { [toggle]: boolean } }. */
  moduleSettings: Record<string, Record<string, boolean>>;
}

export interface GetTeacherDashboardInitialLoadArgs {
  date: string;
}

/**
 * Converts an error to a serializable format for Redux state.
 * Error objects are not serializable, so we extract their properties.
 */
function normalizeError(err: unknown): {
  message: string;
  name?: string;
  code?: string;
} {
  if (err instanceof Error) {
    return {
      message: err.message,
      name: err.name,
      code: (err as any).code,
    };
  }
  if (typeof err === "string") {
    return { message: err };
  }
  if (err && typeof err === "object") {
    const e = err as any;
    return {
      message: e?.message ?? "Unknown error",
      name: e?.name,
      code: e?.code,
    };
  }
  return { message: "Unknown error" };
}

function mapTeacherProfile(raw: any): TeacherProfile {
  if (!raw || !raw.id || !raw.school_id) {
    throw new Error(
      "fn_teacher_dashboard_initial_load returned invalid teacher_profile",
    );
  }
  return {
    id: String(raw.id),
    schoolId: String(raw.school_id),
    firstName: String(raw.first_name ?? ""),
    lastName: String(raw.last_name ?? ""),
    email: raw.email ?? null,
  };
}

function mapRoles(raw: any): TeacherRole[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => ({ id: String(r.id), name: String(r.name ?? "") }))
    .filter((r) => !!r.id && !!r.name);
}

function mapClassLinks(raw: any): TeacherClassLink[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => ({
    classId: String(row.class_id),
    staffId: String(row.staff_id),
    isClassTeacher: Boolean(row.is_class_teacher),
    role: String(row.role ?? ""),
    className: String(row.class_name ?? ""),
  }));
}

function mapRegistrationPeriods(raw: any): RegistrationPeriod[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => ({
    id: String(row.id),
    title: String(row.title ?? ""),
    status: String(row.status ?? ""),
    openedAt: row.opened_at ?? null,
    closedAt: row.closed_at ?? null,
    courseStartDate: row.course_start_date ?? null,
    semesterId: row.semester_id ?? null,
  }));
}

function mapAttendanceBadge(
  raw: any | null | undefined,
): AttendanceBadge | null {
  if (!raw) return null;

  const totalStudents = Number(raw.total_students ?? 0);
  const presentCount = Number(raw.present_count ?? 0);
  const lateCount = Number(raw.late_count ?? 0);
  const absentCount = Number(raw.absent_count ?? 0);

  // Use the view's attendance_status directly — it uses total_logs which
  // correctly counts ALL statuses, whereas individual counts may not cover
  // every enum value (e.g. left_early, temporarily_offsite).
  const attendanceStatus = raw.attendance_status ?? "none";

  return {
    total_students: totalStudents,
    present_count: presentCount,
    late_count: lateCount,
    absent_count: absentCount,
    attendance_status: attendanceStatus,
  };
}

function mapLessons(raw: any): TeacherDashboardLesson[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => ({
    lessonId: String(row.lesson_id),
    schoolId: String(row.school_id),
    lessonDate: String(row.lesson_date),
    startDatetime: String(row.start_datetime),
    endDatetime: String(row.end_datetime),
    primaryTeacherId: row.primary_teacher_id
      ? String(row.primary_teacher_id)
      : null,
    teacherIds: Array.isArray(row.teacher_ids)
      ? row.teacher_ids.map((id: any) => String(id))
      : [],
    teacherKurzels: (() => {
      const kurzels = Array.isArray(row.teacher_kurzels)
        ? row.teacher_kurzels.map((k: any) => String(k))
        : [];
      // For zusätzlicher_einsatz, prepend the original teacher kurzel
      // so both the original teacher and the added teacher are visible
      if (
        row.substitute_type === "zusaetzlicher_einsatz" &&
        row.original_teacher_kurzel &&
        !kurzels.includes(String(row.original_teacher_kurzel))
      ) {
        return [String(row.original_teacher_kurzel), ...kurzels];
      }
      return kurzels;
    })(),
    subjectName: row.subject_name ?? null,
    subjectAbbreviation: row.subject_abbreviation ?? null,
    courseId: row.course_id ? String(row.course_id) : null,
    courseName: row.course_name ?? null,
    classId: row.class_id ? String(row.class_id) : null,
    className: row.class_name ?? null,
    roomId: row.substitute_room_id
      ? String(row.substitute_room_id)
      : row.room_id
      ? String(row.room_id)
      : null,
    roomName: row.substitute_room_name ?? row.room_name ?? null,
    roomNumber: row.substitute_room_number ?? row.room_number ?? null,
    substituteType: row.substitute_type ?? null,
    substitutePrimaryTeacherId: row.substitute_primary_teacher_id
      ? String(row.substitute_primary_teacher_id)
      : null,
    substituteBegleitungTeacherIds: Array.isArray(
      row.substitute_begleitung_teacher_ids,
    )
      ? row.substitute_begleitung_teacher_ids.map((id: any) => String(id))
      : [],
    substituteBegleitungTeacherKurzels: Array.isArray(
      row.substitute_begleitung_teacher_kurzels,
    )
      ? row.substitute_begleitung_teacher_kurzels.map((k: any) => String(k))
      : [],
    notes: row.notes ?? null,
    attendanceTaken: Boolean(row.attendance_taken),
    teacherRole:
      (row.teacher_role as TeacherDashboardLesson["teacherRole"]) ?? "primary",
    // Use IDs for logic (reliable), kürzels for display
    absentTeacherIds: Array.isArray(row.absent_teacher_ids)
      ? row.absent_teacher_ids.map((id: any) => String(id))
      : [],
    absentTeacherKurzels: Array.isArray(row.absent_teacher_kurzels)
      ? row.absent_teacher_kurzels.map((k: any) => String(k))
      : [],
    originalCoteacherKurzels: Array.isArray(row.original_coteacher_kurzels)
      ? row.original_coteacher_kurzels.map((k: any) => String(k))
      : [],
    // Room change tracking
    originalRoomNumber: row.room_number ?? null,
    substituteRoomNumber: row.substitute_room_number ?? null,
    hasRoomChange: !!(
      row.substitute_room_number &&
      row.room_number &&
      row.substitute_room_number !== row.room_number
    ),
    // Substitute comment (from substitutions table)
    substituteComment: row.substitute_comment ?? null,
    // Substitution is_cancelled flag
    substituteIsCancelled: Boolean(row.substitute_is_cancelled),
    attendanceBadge: mapAttendanceBadge(row.attendance_badge),
    teacherAbsenceReason: row.teacher_absence_reason ?? null,
    periodNumber: row.period_number != null ? Number(row.period_number) : null,
    itemType: row.item_type ?? null,
    meetingName: row.meeting_name ?? null,
    bandAssignment: row.band_assignment
      ? {
          assignmentId: String(row.band_assignment.assignment_id),
          bandId: String(row.band_assignment.band_id),
          bandName: String(row.band_assignment.band_name ?? ""),
          bandIcon: row.band_assignment.band_icon ?? null,
          bandColor: row.band_assignment.band_color ?? null,
          completed: Boolean(row.band_assignment.completed),
        }
      : null,
  }));
}

function mapClassAbsences(raw: any): ClassAbsenceRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => ({
    id: String(row.id),
    classIds: Array.isArray(row.class_ids)
      ? row.class_ids.map((id: any) => String(id))
      : [],
    startDate: String(row.start_date),
    endDate: String(row.end_date),
    startTime: row.start_time ?? null,
    endTime: row.end_time ?? null,
    absenceType: row.absence_type ?? null,
    destination: row.destination ?? null,
    accompanyingStaffIds: Array.isArray(row.accompanying_staff_ids)
      ? row.accompanying_staff_ids.map((id: any) => String(id))
      : [],
    classNames: Array.isArray(row.class_names)
      ? row.class_names.map((name: any) => String(name))
      : [],
    attendanceRequired: row.attendance_required !== false,
  }));
}

function mapTripAttendanceCounts(
  raw: any,
): Record<string, TripAttendanceSummary> {
  if (!raw || typeof raw !== "object") return {};
  const result: Record<string, TripAttendanceSummary> = {};
  Object.entries(raw).forEach(([key, value]) => {
    const v = value as any;
    result[key] = {
      present: Number(v.present ?? 0),
      late: Number(v.late ?? 0),
      absent: Number(v.absent ?? 0),
      total: Number(v.total ?? 0),
    };
  });
  return result;
}

export const teacherDashboardApi = createApi({
  reducerPath: "teacherDashboardApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["TeacherDashboard"],
  endpoints: (builder) => ({
    getTeacherDashboardInitialLoad: builder.query<
      TeacherDashboardInitialLoadResponse,
      GetTeacherDashboardInitialLoadArgs
    >({
      async queryFn(args, { dispatch }) {
        try {
          const { date } = args;
          const { data, error } = await supabase.rpc(
            "fn_teacher_dashboard_initial_load" as any,
            {
              p_date: date as any,
            } as any,
          );

          if (error) {
            return { error: normalizeError(error) };
          }

          const payload = Array.isArray(data)
            ? (data[0] as any)
            : (data as any);
          if (!payload) {
            return {
              error: normalizeError(
                new Error("fn_teacher_dashboard_initial_load returned no data"),
              ),
            };
          }

          const response: TeacherDashboardInitialLoadResponse = {
            teacherProfile: mapTeacherProfile(payload.teacher_profile),
            roles: mapRoles(payload.roles),
            classLinks: mapClassLinks(payload.class_links),
            collectingPeriods: mapRegistrationPeriods(
              payload.collecting_periods,
            ),
            wuenscheRegistrationPeriods: mapRegistrationPeriods(
              payload.wuensche_registration_periods,
            ),
            lessonsToday: mapLessons(payload.lessons_today),
            classAbsencesToday: mapClassAbsences(payload.class_absences_today),
            tripAttendanceCounts: mapTripAttendanceCounts(
              payload.trip_attendance_counts,
            ),
            // RPC returns aufsichten[] in the same camelCase shape the
            // useTeacherDashboardData hook expects, no remapping needed.
            aufsichten: Array.isArray(payload.aufsichten)
              ? payload.aufsichten
              : [],
            moduleSettings:
              payload.module_settings &&
              typeof payload.module_settings === "object"
                ? (payload.module_settings as Record<
                    string,
                    Record<string, boolean>
                  >)
                : {},
          };

          // Phase 6e: Hydrate notification cache from bootstrap payload
          // Zero extra requests — piggybacks on the existing dashboard RPC.
          if (payload.notification_bootstrap) {
            const bootstrap =
              payload.notification_bootstrap as NotificationHistoryPayload;
            dispatch(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (notificationsApi.util.upsertQueryData as any)(
                "getMyNotificationHistory",
                undefined,
                {
                  items: bootstrap.items ?? [],
                  unread_count: bootstrap.unread_count ?? 0,
                },
              ),
            );
          }

          return { data: response };
        } catch (error: any) {
          return { error: normalizeError(error) };
        }
      },
      providesTags: (_result, _error, args) => [
        { type: "TeacherDashboard", id: `INITIAL:${args.date}` },
      ],
    }),
  }),
});

export const { useGetTeacherDashboardInitialLoadQuery } = teacherDashboardApi;
